/* import __COLOCATED_TEMPLATE__ from './prism-select-input.hbs'; */
import { empty, not, reads } from '@ember/object/computed';
import Component from '@ember/component';
import { computed } from '@ember/object';

/**
 * This component provides a styled select input field. It is a wrapper around
 * emberx-select and provides an SVG caret that can be styled.
 *
 * Example component usage:
 *
 * hbs
 * ```
 * {{prism-select-input
 *   inputId=input.id
 *   fieldValue=(readonly foobar)
 *   inputClass="prism-form-control prism-select-input--color"
 *   placeholder="String"
 *   onChange=(action "didChange")
 *   options=listOfOptions}}
 * ```
 *
 * @class PrismSelectInput
 */
export default Component.extend({
  classNames: ['prism-select-input'],
  /**
   * ID to apply to the underlying input field.
   *
   * @argument inputId
   * @type {String}
   */
  inputId: null,
  /**
   * Class to apply to the underlying input field.
   *
   * @argument inputClass
   * @type {String}
   */
  inputClass: null,
  /**
   * Placeholder of the underlying input field.
   *
   * @argument placeholder
   * @type {String}
   */
  placeholder: null,
  /**
   * x-select fires the action on `change`. You will need to write an action
   * handler to set the appropriate value in your model.
   *
   * @argument onChange
   * @type {Action}
   */
  onChange() {},
  /**
   * An array of options; an option is a (value, label) pair.
   *
   * For example:
   * `[{value: '1', label: 'January'}, {value: '2', label: 'February'}, ...]``
   *
   * @argument options
   * @type {Array<Object>}
   */
  options: null,
  /**
   * The value of the component's input that should be changed when the select
   * is updated.
   *
   * @argument fieldValue
   * @type {any}
   */
  fieldValue: null,
  /**
   * @argument isDisabled
   * @type {Boolean}
   */
  isDisabled: false,
  didInsertElement(...args) {
    this._super(...args);
    this.set('value', this.fieldValue);
  },
  // Private
  _isPlaceholderEnabled: empty('value'),
  _isPlaceholderDisabled: not('_isPlaceholderEnabled'),
  _isPlaceholderSelected: reads('_isPlaceholderEnabled'),
  _selectElementClassList: computed('inputClass', '_isPlaceholderSelected', function () {
    const inputClass = this.inputClass;
    const isPlaceholderSelected = this._isPlaceholderSelected;
    let classString = `prism-select-input__select ${inputClass}`;
    if (isPlaceholderSelected) {
      classString = `prism-select-input__select--no-selection ${classString}`;
    }
    return classString;
  }),
  _onChange(value, event) {
    this.set('value', value);
    this.onChange(value, event);
  },
  // The x-option component fires an onDisable action when disabled state changes
  // but does not have a default no-op so we must provide one because we do not
  // care about disabled state changing.
  noop() {}
});